import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";
import * as XLSX from "xlsx";
import { classList, houseList, sectionList } from "../../utils";
import { Link } from "react-router-dom";

function SendEmail() {
  const [sentEmail, setSentEmail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const todayDate = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    title: "",
    message: "",
  });

  const [selectedClass, setSelectedClass] = useState([]);
  const [selectedSection, setSelectedSection] = useState([]);
  const [selectedBusNo, setSelectedBusNo] = useState([]);
  const [selectedHouse, setSelectedHouse] = useState([]);
  const [selectedGender, setSelectedGender] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  const allClassOptions = classList.map((item) => ({
    value: item,
    label: item,
  }));
  const allSectionOptions = sectionList.map((item) => ({
    value: item,
    label: item,
  }));
  const allBusNoOptions = Array.from({ length: 20 }, (_, i) => ({
    value: (i + 1).toString().padStart(2, "0"),
    label: (i + 1).toString().padStart(2, "0"),
  }));
  const allHouseOptions = houseList.map((item) => ({
    value: item,
    label: item,
  }));
  const allGenderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
  ];

  const handleMultiSelectChange = (selectedOptions, allOptions, setter) => {
    if (!selectedOptions) {
      setter([]);
      return;
    }

    const isSelectAllSelected = selectedOptions.find(
      (option) => option.value === "*",
    );

    if (isSelectAllSelected) {
      setter(allOptions);
    } else {
      setter(selectedOptions);
    }
  };

  const withSelectAll = (options) => [
    { value: "*", label: "Select All" },
    ...options,
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.message) {
      toast.error("Title and Message are Required", { position: "top-right" });
      return;
    }

    try {
      let attachmentUrl = "";

      if (selectedFile) {
        // Step 1: Get Presigned URL
        const fileName = `${Date.now()}_${selectedFile.name}`;
        const presignResponse = await axios.post(
          "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/email_sent_presigned",
          { fileName: [fileName] },
        );

        const uploadDetails =
          presignResponse?.data?.["body-json"]?.body?.uploadDetails?.[0];
        if (!uploadDetails) {
          throw new Error("Upload details not received");
        }

        const signedUrl = uploadDetails.signedUrl;
        const fileKey = uploadDetails.key;

        if (!signedUrl || !fileKey) {
          throw new Error("Invalid presigned URL or key");
        }

        // Step 2: Upload File to S3
        await axios.put(signedUrl, selectedFile, {
          headers: {
            "Content-Type": selectedFile.type,
          },
        });

        // Step 3: Set attachmentUrl
        attachmentUrl = fileKey;
      }

      // Step 4: Prepare final payload
      const payload = {
        templateId: "8978058",
        title: formData.title,
        message: formData.message,
        attachment: attachmentUrl || "", // leave empty if no file selected
        studentClass: selectedClass.map((item) => item.value).join(","),
        section: selectedSection.map((item) => item.value).join(","),
        busNo: selectedBusNo.map((item) => item.value).join(","),
        house: selectedHouse.map((item) => item.value).join(","),
        gender: selectedGender.map((item) => item.value).join(","),
      };

      // Step 5: Send final POST
      await axios.post(
        "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/email_sent_mongodb",
        payload,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        },
      );

      toast.success("Email Sent", { position: "top-right" });

      // Step 6: Reset form
      setFormData({ title: "", message: "" });
      setSelectedClass([]);
      setSelectedSection([]);
      setSelectedBusNo([]);
      setSelectedHouse([]);
      setSelectedGender([]);
      setSelectedFile(null);
    } catch (error) {
      toast.error("Please Try Again", { position: "top-right" });
    }
  };

  const fetchNotice = async () => {
    try {
      const response = await axios.get(`${server}/email_sent_get_all_mongodb`, {
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
        },
      });
      if (response.data && response.data["body-json"]?.body?.emails) {
        setSentEmail(response.data["body-json"].body.emails);
      } else {
        console.error("Invalid data format from API", response.data);
      }
    } catch (error) {
      console.error("Error fetching bus details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotice();
  }, []);

  // Filter
  const filteredRecords = sentEmail.filter((scholarship) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(scholarship).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower),
        );
      }
      return false;
    });
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleExportAllEmailsExcel = () => {
    if (!sentEmail || sentEmail.length === 0) {
      toast.error("No Email records to export.");
      return;
    }

    const sheetData = sentEmail.map((email) => ({
      Title: email.title || "",
      Class: email.class || "",
      Section: email.section || "",
      "Bus No": email.busNo || "",
      House: email.house || "",
      Gender: email.gender || "",
      Message: email.message || "",
      Attachment: email.attachment || "",
      Date: email.createdAt
        ? new Date(email.createdAt).toLocaleDateString("en-GB")
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All Emails");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AllEmails_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportEmailsWithAttachmentExcel = () => {
    if (!sentEmail || sentEmail.length === 0) {
      toast.error("No Email records to export.");
      return;
    }

    const emailsWithAttachment = sentEmail.filter(
      (email) => email.attachment && email.attachment.trim() !== "",
    );

    if (emailsWithAttachment.length === 0) {
      toast.warning("No emails with attachments found.");
      return;
    }

    const sheetData = emailsWithAttachment.map((email) => ({
      Title: email.title || "",
      Class: email.class || "",
      Section: email.section || "",
      "Bus No": email.busNo || "",
      House: email.house || "",
      Gender: email.gender || "",
      Message: email.message || "",
      Attachment: email.attachment,
      Date: email.createdAt
        ? new Date(email.createdAt).toLocaleDateString("en-GB")
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Emails With Attachment");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Emails_WithAttachment_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            class="modal fade gd-example-modal-lg"
            tabindex="-1"
            role="dialog"
            aria-labelledby="myLargeModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header d-flex justify-content-between">
                    <h5
                      className="modal-title fs-4"
                      id="exampleModalToggleLabel"
                    >
                      Compose Email
                    </h5>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div className="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          checked
                          readOnly
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>

                  <div className="modal-body">
                    <div className="row g-2 mb-2">
                      {/* Title */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Title <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          placeholder="Enter Title"
                          required
                        />
                      </div>

                      {/* Attachment */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Attachment
                        </label>
                        <input
                          type="file"
                          className="form-control text-dark"
                          accept=".pdf,.doc,.docx,.png,.jpg"
                          onChange={handleFileChange}
                        />
                      </div>

                      {/* Message */}
                      <div className="col-12">
                        <label className="form-label mb-1 ms-3">
                          Message <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control text-dark"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Enter Message"
                          rows="5"
                          required
                        ></textarea>
                      </div>

                      {/* Class */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Class <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={withSelectAll(allClassOptions)}
                          isMulti
                          value={selectedClass}
                          onChange={(selected) =>
                            handleMultiSelectChange(
                              selected,
                              allClassOptions,
                              setSelectedClass,
                            )
                          }
                          placeholder="Select Class"
                        />
                      </div>

                      {/* Section */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Section <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={withSelectAll(allSectionOptions)}
                          isMulti
                          value={selectedSection}
                          onChange={(selected) =>
                            handleMultiSelectChange(
                              selected,
                              allSectionOptions,
                              setSelectedSection,
                            )
                          }
                          placeholder="Select Section"
                        />
                      </div>

                      {/* Bus No */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Bus No <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={withSelectAll(allBusNoOptions)}
                          isMulti
                          value={selectedBusNo}
                          onChange={(selected) =>
                            handleMultiSelectChange(
                              selected,
                              allBusNoOptions,
                              setSelectedBusNo,
                            )
                          }
                          placeholder="Select Bus Number"
                        />
                      </div>

                      {/* House */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          House <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={withSelectAll(allHouseOptions)}
                          isMulti
                          value={selectedHouse}
                          onChange={(selected) =>
                            handleMultiSelectChange(
                              selected,
                              allHouseOptions,
                              setSelectedHouse,
                            )
                          }
                          placeholder="Select House"
                        />
                      </div>

                      {/* Gender */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Gender <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={withSelectAll(allGenderOptions)}
                          isMulti
                          value={selectedGender}
                          onChange={(selected) =>
                            handleMultiSelectChange(
                              selected,
                              allGenderOptions,
                              setSelectedGender,
                            )
                          }
                          placeholder="Select Gender"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        className="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        class="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={loading}
                      >
                        {loading ? (
                          <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((email, index) => (
                          <tr>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Title</span> &mdash;{" "}
                              {email.title}
                              <br />
                              <span class="text-dark">Class</span> &mdash;{" "}
                              {email.class}
                              <br />
                              <span class="text-dark">
                                Section
                              </span> &mdash; {email.section}
                              <br />
                              <span class="text-dark">Bus No</span> &mdash;{" "}
                              {email.busNo}
                              <br />
                              <span class="text-dark">House</span> &mdash;{" "}
                              {email.house}
                              <br />
                              <span class="text-dark">Gender</span> &mdash;{" "}
                              {email.gender}
                              <br />
                              <span class="text-dark">
                                Message
                              </span> &mdash;{" "}
                              {email.message
                                .split(" ")
                                .reduce((acc, word, index) => {
                                  const last = acc.length - 1;
                                  if (index % 5 === 0) {
                                    acc.push([word]);
                                  } else {
                                    acc[last].push(word);
                                  }
                                  return acc;
                                }, [])
                                .map((group, i) => (
                                  <span key={i}>
                                    {group.join(" ")}
                                    <br />
                                  </span>
                                ))}
                              <br />
                              <span class="text-dark">
                                Attachment
                              </span> &mdash;{" "}
                              <a href={email.attachment} target="_blank">
                                View File
                              </a>
                              <br />
                              <span class="text-dark">Date</span> &mdash;{" "}
                              {email.createdAt
                                ? new Date(email.createdAt).toLocaleDateString(
                                    "en-GB",
                                  )
                                : ""}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                  E-Mail Sent
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          data-bs-toggle="modal"
                          data-bs-target=".gd-example-modal-lg"
                          class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Compose Email
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Communicate</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Send Email
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}
                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Send Email
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              0.2 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportAllEmailsExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  All Mails
                                </span>
                                <span
                                  onClick={
                                    handleExportEmailsWithAttachmentExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Mails With Attachment
                                </span>
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  Date Wise E-Mail
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default SendEmail;
